import { MailboxDraftFieldsFragment } from '~/api/graphql';

/**
 * `live` is the only state where something is actually sitting in a Drafts
 * folder; the rest describe a row that is now only a record of one.
 */
export type MailboxDraftState =
  | 'live'
  | 'sentViaCara'
  | 'sentExternally'
  | 'deletedExternally'
  | 'superseded'
  | 'retracted'
  | 'retired';

/** Reasons the runtime writes; see 00067_mailbox_ledgers. Unknown values fall through to 'retired'. */
const RETIRED_REASON_STATES: Record<string, MailboxDraftState> = {
  sent_via_cara: 'sentViaCara',
  sent_externally: 'sentExternally',
  deleted_externally: 'deletedExternally',
  superseded: 'superseded',
  retracted: 'retracted'
};

export const mailboxDraftState = (
  draft: Pick<MailboxDraftFieldsFragment, 'retiredAt' | 'retiredReason'>
): MailboxDraftState =>
  draft.retiredAt ? (RETIRED_REASON_STATES[draft.retiredReason] ?? 'retired') : 'live';

/** As outboundEmailArtifactHref, for the version the draft was written from. */
export const mailboxDraftArtifactHref = (
  draft: Pick<MailboxDraftFieldsFragment, 'workspaceId' | 'workspaceArtifactId' | 'artifactVersion'>
): string | undefined =>
  draft.workspaceId && draft.workspaceArtifactId
    ? `/app/workspaces/${draft.workspaceId}?artifact_id=${draft.workspaceArtifactId}&version=${draft.artifactVersion}`
    : undefined;

/**
 * The provider's own link. Live drafts only: a retired row's link addresses a
 * message that has moved or gone.
 */
export const mailboxDraftWebLink = (
  draft: Pick<MailboxDraftFieldsFragment, 'webLink' | 'retiredAt' | 'retiredReason'>
): string | undefined => {
  if (mailboxDraftState(draft) !== 'live') {
    return undefined;
  }
  return draft.webLink.trim() || undefined;
};
